import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import template from './ns-adp-add.html';
import {AdrService, ProductService, ShippingService} from '@nuskin/ns-shop';
import {dateFormatter, RunConfigService, storage, UrlService, ConfigService} from '@nuskin/ns-util';
import {AccountManager} from '@nuskin/ns-account';
import {mockAdrList} from './mockAdrList.js';

const dfltStrings = {
    selectLabel: 'Existing {ADR}s',
    buttonLabel: 'Add to Existing {ADR}',
    itemsStr: '- {num} item(s)',
    programName: 'ADR'
};

/**
 * Implements slotted-dropdown to display a list of ADR's available to user.
 * Appears on Ecom Cart when: a user is logged in & has available ADR's & market configuration is set up to allow dropdown to appear.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h1>ADP Add Component Demo</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-adp-add is-demo></ns-adp-add>
 *   </template>
 * </demo-snippet>
 */
export class NsAdpAdd extends PolymerElement {
    static get is() {
        return 'ns-adp-add';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * Language code
             */
            language: {
                type: String,
                value: 'en'
            },
            /**
             * Country code
             */
            country: {
                type: String,
                value: 'US'
            },
            /**
             * Populates component with test data and prevents any unnecessary network calls.
             * Add to ADP button is disabled in this mode.
             */
            isDemo: {
                type: Boolean,
                value: false
            },
            translations: {
                type: Object,
                value: dfltStrings,
                observer: '_translations'
            },

            loadAdr: {
                type: Boolean,
                value: false,
                observer: 'setList'
            },

            /**
             * Hidden flag, reflected to DOM. By default attr. is false and not hidden
             */
            hidden: {
                type: Boolean,
                value: false,
                reflectToAttribute: true
            },
            /**
             * Length to trim strings until
             * Used by component to trim selectedItem.value to slotted dropdown
             * Binded to slotted dropdowns trimLength property to be used for selectedText
             */
            trimLength: {
                type: Number,
                value: 75
            },
            /**
             * Value passed through from ns-dropdown, index of selected element in adrList
             */
            selectedIndex: {
                type: Number,
                notify: true
            },
            /**
             * List of ADRs available, populates dropdown
             */
            adrList: {
                type: Array,
                notify: true
            }
        };
    }

    static get observers() {
        return ['_updateDropdownLabel(translations, adrList)'];
    }

    /**
     * Webcomponent LifeCycle Method
     * Sets up component in ready to:
     * Exposes Services used on component
     * Sets base properties on component including: language, country, and translations.
     * Populate dropdown via setList()
     */
    ready() {
        super.ready();
        this.AdrService = AdrService;
        this.ProductService = ProductService;
        this.dropdown = this.$.adrDropdown;
    }

    /**
     * Webcomponent LifeCycle Method
     * Add event listener, nsDropdownSelect - from ns-slotted-dropdown
     */
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('nsDropdownSelect', this._dropDownChange);
    }

    /**
     * Webcomponent LifeCycle Method
     * Remove event listener, nsDropdownSelect - from ns-slotted-dropdown
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('nsDropdownSelect', this._dropDownChange);
    }

    /**
     * Determines where to retrieve adrList, either from adrService or mock data
     * List comes from service if user is logged in and isDemo flag is off
     */
    setList() {
        if (this.loadAdr) {
            (AccountManager.isLoggedIn() !== null && !this.isDemo) ? this._setListResponse() : this._setMockAdrList();
        }
    }

    /**
     * Sets a mock processed adrList for test/demo purposes
     */
    _setMockAdrList() {
        this.adrList = mockAdrList;
    }

    /**
     * Calls AdrService to get adrList and process the list response and finally sets processsed list.
     * Processed list, adrList, is the array passed to slotted-dropdown.
     * OPTIONS is set up to use cached data, forceRefresh, and a 'cart' context.
     * Full qualified item names are queried by the ProductService
     */
    _setListResponse() {
        if (!this.adrList) {
            this.set('adrList', []);
            const OPTIONS = {
                context: 'cart',
                forceRefresh: false
            };
            const ITEM_SKU_REGEX = '^[0-9]+$';
            const JOIN_DELIMITTER = ', ';

            this.hidden = true;

            AdrService.getAdrList(OPTIONS)
                .then(async listResponse => {
                    let {adrMap, skuMap} = await this._parseAdrList(listResponse),
                        productTitleMap = {},
                        promises = Object.keys(skuMap).map(sku => {
                            if (sku.match(ITEM_SKU_REGEX)) {
                                return ProductService.getProductData(sku)
                                    .then(res => {
                                        productTitleMap[res.sku] = res.title;
                                    })
                                    .catch(err => {
                                        console.warn(err);
                                        productTitleMap[sku] = sku;
                                    });
                            }
                        });

                    return Promise.all(promises).then(() => {
                        let list = [],
                            itemsStr = this.itemsStr || dfltStrings.itemsStr;

                        Object.keys(adrMap).forEach((adrNum,i) => {
                            let valueStr = adrMap[adrNum].isEditable ? '' : this.processingStr;
                            valueStr += `${this.programName}# ${adrNum}  ${itemsStr.replace('{num}', adrMap[adrNum].skus.length)}`;
                            list.push({
                                key: adrNum,
                                numItems: adrMap[adrNum].skus.length,
                                value: this._trim(valueStr),
                                isEditable: adrMap[adrNum].isEditable,
                                adrName: this._trim(this._decodeHTML(adrMap[adrNum].skus.map(sku => productTitleMap[sku]).join(JOIN_DELIMITTER)))
                            });
                            if (adrMap[adrNum].isEditable && !this.selectedIndex) {
                                this.selectedIndex = i;
                            }
                        });

                        this.hidden = false;
                        this.set('adrList', list);
                    });
                })
                .catch(err => {
                    console.error(err);
                    this.hidden = true;
                });
        }
    }

    /**
     * Parses through an ADR response list into an ADR map of ADR Numbers to SKU's
     *  and an Item map of SKU's to ADR's
     *
     * @param adrList  List of ADR's from service request
     * @returns {Promise<{adrMap, skuMap}>}
     * @private
     */
    async _parseAdrList(adrList) {
        let adrMap = {},
            skuMap = {};

        for (let i = 0; i < adrList.length; i++) {
            let adr = adrList[i];
            let adrId = adr.SalesOrderDetail.OrderID;

            adrMap[adrId] = {
                skus: [],
                isEditable: await this._setEditAndOverrideValues(adr)
            };

            adr.SalesOrderDetail.LineItemDetails
                .filter(item => item.LineItem.ParentID === 0)
                .forEach(item => {
                    adrMap[adrId].skus.push(item.LineItem.SKU);
                    if (skuMap[item.LineItem.SKU]) {
                        skuMap[item.LineItem.SKU].push(adrId);
                    } else {
                        skuMap[item.LineItem.SKU] = [adrId];
                    }
                });
        }

        return {adrMap, skuMap};
    }

    /**
     * Finds index of adrId in adrList, returns 0 when findIndex fails
     * @param {String} [adrId] - Id of ADR
     * @return {Number} index - index of ADR in adrList - Returns and index of 0 if not found
     */
    _findIndexByAdrId(adrId) {
        const INDEX = this.adrList.findIndex(adr => adr.key === adrId);
        return INDEX > -1 ? INDEX : 0;
    }

    /**
     * Helper function to trim Strings
     * @param {String} [str] - String to potentially trim
     * @return {String} [strToReturn] - Trimmed String... || Original String
     */
    _trim(str) {
        return (str.length > this.trimLength) ? `${str.substring(0, this.trimLength)}&hellip` : str;
    }

    /**
     * Helper method decodes HTML entities
     * HTML tags will still be in the returned string,
     * set innerHTML of <paper-item-body> on template to write to dropdown
     * @param {String} [str] - String to decode HTML entities
     * @return {String} [decodedStr] - The Decoded String to return
     */
    _decodeHTML(str) {
        return ($('<textarea />').html(str).text());
    }

    /**
     * Get IS_EDITABLE, IS_OVERRIDABLE
     * Edit buttons should show when:
     *   adr.held = false and is not within the adr blackout period.
     * Override button should show when:
     *   IS_EDITABLE = true, adr.overridden = false and order has not been placed this month
     * @param {ADR} [adr] - an order, or ADR from getAdrList response
     * @return {Object} [FLAGS] - returns an object with flags about ADR
     */
    async _setEditAndOverrideValues(adr) {
        const BLACKOUT_DATE = new Date();
        let blackoutPeriod = 1, // default value, used when ship days does not return a valid response.
            isEditable = false;

        await ShippingService.getShipDays()
            .then(response => {
                blackoutPeriod = response.ShipDaysResponse.blackoutPeriod;
            })
            .catch(console.error)
            .finally(() => {
                BLACKOUT_DATE.setDate(BLACKOUT_DATE.getDate() + blackoutPeriod);
                const BLACKOUT_HQ_DATE = new Date(dateFormatter.toHQTimeStartOfDay(BLACKOUT_DATE)).getTime();
                const IS_BLOCKED_DATE = adr.NextDeliveryDate <= BLACKOUT_HQ_DATE;
                const IS_ON_HOLD = adr.Held;
                isEditable = (adr.Editable || !(IS_ON_HOLD || IS_BLOCKED_DATE));
            });
        return isEditable;
    }

    /**
     * Event Listener for slotted-dropdown on dropdown change event, 'nsDropdownSelect'
     * Sets selectedIndex and selectedText property
     * @param {event} [e] - nsDropdownSelect event
     */
    _dropDownChange(e) {
        this.selectedIndex = this._findIndexByAdrId(e.detail.selectedItem.value);
        this.selectedText = this.adrList[this.selectedIndex].value;
        this.isNotEditable = !this.adrList[this.selectedIndex].isEditable;
    }

    /**
     * Issue in Iron-Menu-Behavior where observer for selected change does not get triggered because value was never changed.
     * This will close dropdown if user selects, pre-selected item.
     * @param {Event} [e] - annotated onTap event for paper-item in template
     */
    paperItemTap(e) {
        if (e.model.__data.index === this.selectedIndex) {
            e.preventDefault();
            this.$.adrDropdown.close();
        }
    }

    /**
     * On-Tap event listener for button on component template
     * Sets currently adrList selectedIndex's orderId into storage
     * and redirects user to ADR Edit Cart page, edit products page.
     */
    addToADP() {
        // Short-circuit and return if we match any of the cases below
        if (this.selectedIndex === undefined || !this.adrList[this.selectedIndex].key || !this.adrList[this.selectedIndex].isEditable || this.isDemo) {
            return;
        }

        storage.setItem(storage.metadata.ADR_CURRENT_ID, this.adrList[this.selectedIndex].key, {});

        const defaultEditPage = 'shop/adp-edit.html';
        let adpEditPage = ConfigService.getMarketConfig().adpManage.adpEditPageLocation || defaultEditPage;
        let addHtml = path => path.endsWith('.html') ? path : `${path}.html`;

        window.location = `${UrlService.getAemLocaleUrl()}${addHtml(adpEditPage)}?add=cart`;
    }

    _translations(trans) {
        if (typeof trans === 'object') {
            this.programName = typeof trans.programName === 'string' ? trans.programName : dfltStrings.programName;
            this.itemsStr = typeof trans.itemsStr === 'string' ? trans.itemsStr : dfltStrings.itemsStr;
            this.processingStr = typeof trans.processingStr === 'string' ? trans.processingStr : '';
            this.buttonLabel = typeof trans.buttonLabel === 'string' ? trans.buttonLabel : dfltStrings.buttonLabel.replace('{ADR}', this.programName);

            // update adr list with new translation
            if (this.adrList) {
                this.adrList.forEach(adr => {
                    adr.value = this._trim(adr.isEditable ? `${this.programName}# ${adr.key} ${this.itemsStr.replace('{num}', adr.numItems)}` :
                        `${this.processingStr} ${this.programName}# ${adr.key} ${this.itemsStr.replace('{num}', adr.numItems)}`);
                });
            }
        }
    }

    _updateDropdownLabel(translations, adrs) {
        const programName = translations.programName || dfltStrings.programName;
        const selectLabel = (translations.selectLabel || dfltStrings.selectLabel).replace('{ADR}', programName);
        this.dropdownLabel = selectLabel.replace('{num}', (adrs || []).length);
    }
}

customElements.define(NsAdpAdd.is, NsAdpAdd);
